import { LOCALES, LOCALE_NAME, useLocale, useT, type Locale } from '../../lib/i18n'
import styles from './Help.module.css'

/**
 * THE UNLOCKED DOOR — the page (PARATEXT.md §1; frame law, revival §44).
 *
 * The un-costumed half of the crisis floor. Brief §10 requires these resources to exist OUTSIDE
 * the fiction as well as inside it, so that **the story is never the only door to real help**.
 *
 * ⚠ FOUR LAWS, and each is a thing this file must not grow:
 *
 *  1. **NEVER BEHIND A LOGIN.** Registered in App.tsx above the auth branch, so it renders before
 *     anything asks who you are. A person in crisis must not meet a sign-in wall.
 *  2. **NOTHING IN ANY CHARACTER'S VOICE.** Not Bergur's, not the house's, not the queen's. This
 *     is the product speaking plainly about itself. No fiction, no register, no warmth performed.
 *  3. **OPENING IT TELLS NO ONE.** No analytics, no session ping, no `/activity`, no state
 *     written, no navigation event. The page is inert by construction — there is nothing here
 *     that could report, and nothing may be added that could.
 *  4. **SHORT, AND STATIC.** A person in crisis reads little. No accordion, no tabs, no
 *     progressive disclosure, no fetch.
 *
 * ⚠ THE NUMBERS ARE FOUNDER-OWNED — 112 · 1717 / 1717.is · Píeta samtökin 552-2218, verified by
 * the founder 2026-08-20. Any change is re-verified by him, never sourced from a model. They are
 * identical here and in the in-fiction floor on purpose: two doors, one set of numbers.
 *
 * Copy is PARATEXT.md §1 verbatim in three tongues. ⚠⚠ `is` and ⚠ `es` await their native ear.
 */
export default function Help() {
  const { locale, setLocale } = useLocale()
  const t = useT()
  // English is the floor here, and non-optional: PAGE.en is always present, so this cannot be
  // undefined — but the map is Partial by design (pl/no are deliberately unauthored), which the
  // compiler cannot know. The `!` sits on the base, never on the lookup.
  const copy = PAGE[locale] ?? PAGE.en!

  return (
    <main className={styles.page}>
      <article className={styles.sheet}>
        <h1 className={styles.head}>{copy.title}</h1>
        {copy.body.map((para, i) => (
          <p key={i} className={styles.para}>
            {render(para)}
          </p>
        ))}
      </article>

      {/*
        THE WAY BACK. The page promises it in its own last line — *the story will be where you
        left it whenever you come back* — and until now gave the reader nothing to come back
        with: no link on the page at all, only the browser's back button, which on a phone in
        standalone mode may not be visible.

        ⚠ THE LABEL IS REUSED, NOT AUTHORED. `shelf.theList` is the product's existing word for
        where this goes, already written per language by the pen. A builder inventing a phrase
        for a safety page is exactly what the paratext rules forbid, and reusing an authored one
        costs nothing. One line to replace if Sanctum wants its own words here.

        A real navigation, not a router push: this page renders outside the router, so following
        it re-enters the app fresh — which is also what makes the promise true, because the
        thread resumes on mount.
      */}
      <a className={styles.back} href="/">
        {t('shelf.theList')}
      </a>

      {/*
        The language control is a plain row of words, not the app's picker: the picker is chrome
        that belongs to the product's surfaces, and this page is deliberately outside them. It
        writes the same stored preference, because a person who switches here should not have to
        switch again when they go back.
      */}
      <nav className={styles.tongues} aria-label="language">
        {LOCALES.filter((l) => l in PAGE).map((l) => (
          <button
            key={l}
            className={l === locale ? styles.tongueHere : styles.tongue}
            onClick={() => setLocale(l)}
          >
            {LOCALE_NAME[l]}
          </button>
        ))}
      </nav>
    </main>
  )
}

/**
 * ⚠ THE DIALABLE NUMBERS — FOUNDER-OWNED, AND THIS FILE HOLDS EXACTLY ONE COPY OF EACH.
 *
 * The numbers are founder-verified (112 · 1717 / 1717.is · Píeta samtökin 552-2218, 2026-08-20)
 * and any change is re-verified by him, never sourced from a model. So they are NOT written a
 * second time as `tel:` hrefs: the href is derived from the very characters on the page, which
 * makes it impossible for the link and the label to disagree. A hand-written href is a second
 * copy of a safety-critical number, and second copies drift.
 *
 * ⚠ ORDER MATTERS — `1717.is` must be tried before `1717`, or the chat address becomes a phone
 * call to the first four characters of itself.
 *
 * ⚠ THE DIAL STRING IS THE DIGITS AS GIVEN, with no country code added. Prefixing +354 would be
 * a correct inference and still an addition to a number somebody verified; the page already
 * scopes them ("These numbers are Icelandic") and tells a reader elsewhere to use their own.
 * If international dialling is wanted, that is the founder's call and one edit here.
 */
const DIALABLE = /(1717\.is|\b112\b|\b1717\b|\b552-2218\b)/g

/** One authored paragraph, as nodes: `<strong>` honoured, numbers made touchable. */
function render(para: string) {
  return para.split(/(<strong>.*?<\/strong>)/g).filter(Boolean).map((piece, i) => {
    const bold = piece.startsWith('<strong>')
    const inner = bold ? piece.slice(8, -9) : piece
    const nodes = link(inner)
    return bold ? <strong key={i}>{nodes}</strong> : <span key={i}>{nodes}</span>
  })
}

/**
 * The one interaction on the page. A person in crisis, on a phone, at three in the morning
 * should not have to transcribe a number into a dialler — that is precisely the moment a tap
 * beats a memory. `tel:` and `https:` are still static, still tell us nothing, and are still in
 * nobody's voice, so all four of the page's laws hold.
 */
function link(text: string) {
  return text.split(DIALABLE).filter(Boolean).map((tok, i) => {
    if (tok === '1717.is') return <a key={i} className="num" href="https://1717.is">{tok}</a>
    if (/^(112|1717|552-2218)$/.test(tok))
      return <a key={i} className="num" href={`tel:${tok.replace(/\D/g, '')}`}>{tok}</a>
    return <span key={i}>{tok}</span>
  })
}

/**
 * PARATEXT.md §1, verbatim. `<strong>` only — the numbers carry the emphasis the source gives
 * them, and nothing else here is marked up. Deliberately not a markdown renderer: this page must
 * not acquire a parser it could one day fail inside.
 *
 * ⚠⚠ ALL FOUR NON-ENGLISH ROWS ARE STRAWMEN awaiting a native ear (PARATEXT §1). The map stays
 * `Partial` deliberately: a language with no row here falls back to English, which is the honest
 * state and the only safe one — a machine-translated crisis page is exactly what the
 * no-runtime-translation law exists to prevent.
 */
const PAGE: Partial<Record<Locale, { title: string; body: string[] }>> = {
  en: {
    title: 'Outside the story',
    body: [
      'If you are in danger right now, call <strong>112</strong>.',
      'If you need to talk to a person tonight: <strong>1717</strong> — the Red Cross helpline. Free, open day and night, no name needed. Also by chat at <strong>1717.is</strong>.',
      'If you are thinking of ending your life: <strong>Píeta samtökin — 552-2218</strong>, day and night.',
      'These numbers are Icelandic. If you are somewhere else, your local emergency number and a local helpline will reach you faster than anything here.',
      '<strong>What this is, plainly.</strong> Hildur is a story you write with. The people in it are fiction; the listening is real. It is not therapy, not a doctor, and not a crisis service, and it does not replace people who are trained for exactly this. It is for adults.',
      'Opening this page tells no one. The story will be where you left it whenever you come back.',
    ],
  },
  is: {
    title: 'Utan við söguna',
    body: [
      'Ef þú ert í hættu núna, hringdu í <strong>112</strong>.',
      'Ef þú þarft að tala við manneskju í nótt: <strong>1717</strong> — Hjálparsími Rauða krossins. Gjaldfrjáls, opinn allan sólarhringinn, engin þörf á að gefa upp nafn. Líka netspjall á <strong>1717.is</strong>.',
      'Ef þú hugsar um að enda líf þitt: <strong>Píeta samtökin — 552-2218</strong>, allan sólarhringinn.',
      'Þessi númer eru íslensk. Ef þú ert annars staðar ná neyðarnúmer og hjálparsími þíns lands til þín hraðar en nokkuð hér.',
      '<strong>Hvað þetta er, sagt hreint út.</strong> Hildur er saga sem þú skrifar með. Fólkið í henni er skáldskapur; hlustunin er raunveruleg. Þetta er ekki meðferð, ekki læknir og ekki neyðarþjónusta, og kemur ekki í staðinn fyrir fólk sem er þjálfað í nákvæmlega þessu. Þetta er fyrir fullorðna.',
      'Enginn fær að vita að þú opnaðir þessa síðu. Sagan bíður þar sem þú skildir við hana, hvenær sem þú kemur aftur.',
    ],
  },
  es: {
    title: 'Fuera de la historia',
    body: [
      'Si estás en peligro ahora mismo, llama al <strong>112</strong>.',
      'Si necesitas hablar con una persona esta noche: <strong>1717</strong> — la línea de ayuda de la Cruz Roja islandesa. Gratuita, abierta día y noche, sin dar tu nombre. También por chat en <strong>1717.is</strong>.',
      'Si estás pensando en quitarte la vida: <strong>Píeta — 552-2218</strong>, día y noche.',
      'Estos números son de Islandia. Si estás en otro lugar, el número de emergencias y una línea de ayuda de tu país llegarán a ti antes que nada de lo que hay aquí.',
      '<strong>Qué es esto, dicho claramente.</strong> Hildur es una historia que escribes con otros. La gente que hay en ella es ficción; la escucha es real. No es terapia, ni un médico, ni un servicio de emergencias, y no sustituye a las personas formadas exactamente para esto. Es para adultos.',
      'Abrir esta página no avisa a nadie. La historia estará donde la dejaste cuando vuelvas.',
    ],
  },
  pl: {
    title: 'Poza opowieścią',
    body: [
      'Jeśli jesteś teraz w niebezpieczeństwie, zadzwoń pod <strong>112</strong>.',
      'Jeśli potrzebujesz dziś porozmawiać z człowiekiem: <strong>1717</strong> — telefon zaufania islandzkiego Czerwonego Krzyża. Bezpłatny, czynny dzień i noc, bez podawania imienia. Także przez czat na <strong>1717.is</strong>.',
      'Jeśli myślisz o odebraniu sobie życia: <strong>Píeta samtökin — 552-2218</strong>, dzień i noc.',
      'Te numery są islandzkie. Jeśli jesteś gdzie indziej, numer alarmowy i telefon zaufania w twoim kraju dotrą do ciebie szybciej niż cokolwiek tutaj.',
      '<strong>Czym to jest, mówiąc wprost.</strong> Hildur to opowieść, którą piszesz razem z innymi. Ludzie w niej są fikcją; słuchanie jest prawdziwe. To nie jest terapia, nie lekarz ani pogotowie, i nie zastępuje ludzi wyszkolonych właśnie do tego. To jest dla dorosłych.',
      'Otwarcie tej strony nikogo nie powiadamia. Opowieść będzie tam, gdzie ją zostawisz, kiedy wrócisz.',
    ],
  },
  no: {
    title: 'Utenfor historien',
    body: [
      'Hvis du er i fare akkurat nå, ring <strong>112</strong>.',
      'Hvis du trenger å snakke med et menneske i kveld: <strong>1717</strong> — hjelpetelefonen til Røde Kors på Island. Gratis, åpen dag og natt, ingen må oppgi navn. Også på chat på <strong>1717.is</strong>.',
      'Hvis du tenker på å ta ditt eget liv: <strong>Píeta samtökin — 552-2218</strong>, dag og natt.',
      'Disse numrene er islandske. Er du et annet sted, når nødnummeret og en hjelpetelefon i ditt eget land deg raskere enn noe her.',
      '<strong>Hva dette er, sagt rett ut.</strong> Hildur er en historie du skriver sammen med andre. Menneskene i den er oppdiktet; lyttingen er ekte. Det er ikke terapi, ikke en lege og ikke en krisetjeneste, og det erstatter ikke folk som er trent nettopp til dette. Det er for voksne.',
      'Å åpne denne siden forteller ingen noe. Historien er der du forlot den, når du kommer tilbake.',
    ],
  },
}
